import React, { useCallback, useMemo, useState } from 'react';
import { useLazyQuery } from 'apollo-client-ppc';
import { useParams } from 'react-router';
import defaultTo from 'lodash/defaultTo';
import uniq from 'lodash/uniq';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import keyBy from 'lodash/keyBy';
import isEmpty from 'lodash/isEmpty';
import { v4 as generateUuid } from 'uuid';
import Drawer from '@atlaskit/drawer';
import { SkeletonItem } from '@atlaskit/menu';
import Tooltip from '@atlaskit/tooltip';
import { Appearance, LoadingButton } from '@atlaskit/button';
import AddIcon from '@atlaskit/icon/glyph/add';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { Y300 } from '@atlaskit/theme/colors';
import { PartnerProductCatalogueItem } from '@atlassian/cssxp-types-product';
import { ParentEntitlementDetails } from '@css-xp/ppc-checkout/src/types';
import { SourceSystem } from '@atlassian/cssxp-typescommon';
import { useFeatureToggle } from '@css-xp/base';
import { isExistingStandaloneJPDProduct, isProductBelongsToJiraFamily } from '@css-xp/ppc-utils';
import CataloguePage from './components/catalogue-search-page';
import { CartLinkedEntitlementsSchema, CartProductSchema, OperationType } from '../../../types';
import { transformLineItemsToCartApp, transformLineItemsToCartProduct } from '../../../utils';
import {
  useAddNewProductStore,
  useCurrentOperationTypesStore,
  useOfferingsAndPricingPlansStore,
  useProductsForCloudSiteStore,
  useStore,
} from '../../../store';
import { CartCatalogueLineItem, CatalogueContextItems } from './types';
import { CloudSiteSelector } from './components/cloud-site-selector';
import { fetchAvailableProductIdsByCloudSiteUrl, fetchAvailableProductIdsOnCloudSite } from './api';
import { FetchEntitlementsForCartQueryType, FetchEntitlementsForCartQueryVariables } from '@css-xp/ppc-cart-common';
import { fetchEntitlementsForCart } from '../../../queries/fetch-entitlements';
import { maxThresholdForResults, ROVO_PRODUCT_ID, STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';
import { getDataForStore } from '../../billing-profile/src/store-utils';
import { CONTAINER_PRODUCT_ID as JIRA_CONTAINER_PRODUCT_ID, isNewEntitlement } from '../../../store/utils';
import { convertToExternalCloudSites } from '../../../store/utils/external-cloud-site-products-and-apps';
import { getTimestampFromLinkedProductsOrApps } from '../../../store/utils/get-timestamp-from-linked-products-apps';
import { commonEntitlementQueryFilters } from '../../billing-profile/src/common-entitlement-query-filters';
import { NEW_CLOUD_SITE, NEW_CLOUD_SITE_ID } from '../../../constants';

interface AddNewProductWrapperProps {
  cloudSiteHost: string;
  isDisabled?: boolean;
  cloudSiteId?: string;
  addNewProductLabel?: string;
  addNewProductBtnAppearance?: Appearance;
  isNetNewSite?: boolean;
  hideAddNewButtonLabel?: boolean;
  showCatalogueInModal?: boolean;
  checkedProductsList?: PartnerProductCatalogueItem[];
  setCheckedProductsList?: (checkedProductsList: PartnerProductCatalogueItem[]) => void;
  handleAddNewAppsClick?: (params: {
    product: any;
    cloudSiteId: string;
    cloudSiteUrl: string;
    onAddToCart: (OrderItems: CatalogueContextItems) => void;
    sandboxCloudSiteParentProductIds: string[];
  }) => void;
  product?: any;
  tooltipContent?: string;
  hideIconBefore?: boolean;
  showLoadingIndicator?: boolean;
}

export const getContainerIdFromExistingJiraProducts = ({
  existingProductsForCloudSite,
  existingFamilyContainerId,
  isJPDAsJiraFamilyEnabled,
}: {
  existingProductsForCloudSite: CartProductSchema[];
  existingFamilyContainerId?: string | undefined;
  isJPDAsJiraFamilyEnabled: boolean;
}) => {
  const cloudSiteJiraProduct = existingProductsForCloudSite?.find((product) => {
    return isProductBelongsToJiraFamily(product?.productId, { isJPDAsJiraFamilyEnabled });
  });
  return cloudSiteJiraProduct?.containerId || existingFamilyContainerId;
};

export const getExistingJiraProduct = ({
  existingProductsForCloudSite,
  isJPDAsJiraFamilyEnabled,
}: {
  existingProductsForCloudSite: CartProductSchema[];
  isJPDAsJiraFamilyEnabled: boolean;
}) => {
  return existingProductsForCloudSite?.find((product) => {
    return isProductBelongsToJiraFamily(product?.productId, { isJPDAsJiraFamilyEnabled }) && !isNewEntitlement(product);
  });
};

const defaultAddNewProductLabel = 'Add new';
export const AddNewProductsWrapper = ({
  cloudSiteHost,
  isDisabled,
  cloudSiteId,
  addNewProductLabel = defaultAddNewProductLabel,
  addNewProductBtnAppearance = 'default',
  isNetNewSite = false,
  hideAddNewButtonLabel = false,
  showCatalogueInModal = false,
  checkedProductsList,
  setCheckedProductsList,
  handleAddNewAppsClick,
  product,
  tooltipContent,
  hideIconBefore,
  showLoadingIndicator = false,
}: AddNewProductWrapperProps) => {
  const { txaId } = useParams<{
    txaId: string;
  }>();
  const [isEnabled] = useFeatureToggle();

  const isJPDAsJiraFamilyEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_JPD_AS_JIRA_FAMILY);
  const isSandboxAppCreateInUOFEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_SANDBOX_APP_CREATE_IN_UOF);
  const isAddOnsPhase2Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_ADD_ONS_PHASE_V2);

  const [isFetchingProductIds, setFetchingProductIds] = useState<boolean>(false);
  const [errorForFetchingCloudSiteProductsIds, setErrorForFetchingCloudSiteProductsIds] = useState<string | null>(null);
  const [existingCloudSiteProductsIds, setExistingCloudSiteProductsIds] = useState<string[]>([]);
  const [isCatalogDrawerOpen, setIsCatalogDrawerOpen] = useState<boolean>(false);
  const [isCloudsiteSelectorOpen, setIsCloudSiteSelectorOpen] = useState(false);
  const [userSelectedCloudSiteHostname, setUserSelectedCloudSiteHostname] = useState('');
  const [existingFamilyContainerId, setExistingFamilyContainerId] = useState<string | undefined>();
  const [cloudSiteIdOfTheUserSelectedCloudSite, setCloudSiteIdOfTheUserSelectedCloudSite] = useState<
    string | undefined
  >();
  const [existingSandboxCloudSiteProductIds, setExistingSandboxCloudSiteProductIds] = useState<string[]>([]);

  const [
    { nativeProduct, isLoading, products, linkedEntitlements, estimatedUsersForRecurringBillEstimate },
    actions,
  ] = useStore();

  const offeringsAndPricingPlans = useOfferingsAndPricingPlansStore();

  let newSiteProductIds: string[] = [];

  if (cloudSiteHost === NEW_CLOUD_SITE && cloudSiteId === NEW_CLOUD_SITE_ID) {
    // newSiteProductIds will provide a list of productIds for already selected items for a new site.
    // Adding a check here because in the new designs, we are directly showing this component instead of site selection when the user clicks on the add new button in the cart.
    newSiteProductIds =
      Object.values(products)
        .filter((product) => product.operationType === OperationType.NEW && isEmpty(product.cloudSite))
        .map(({ productId }) => productId) ?? [];
  }

  const [fetchEntitlementsForCloudSite] = useLazyQuery<
    FetchEntitlementsForCartQueryType,
    FetchEntitlementsForCartQueryVariables
  >(fetchEntitlementsForCart);

  const isJiraOnTransferOperationForCloudSite = useMemo(() => {
    const productsGroupedByCloudSite = groupBy(products, 'cloudSite');
    return productsGroupedByCloudSite?.[cloudSiteHost]?.some(
      // checking if jira family is transferring for the cloud site
      (product) =>
        isProductBelongsToJiraFamily(product?.productId, { isJPDAsJiraFamilyEnabled }) &&
        product?.operationType === OperationType.TRANSFER
    );
  }, [products, cloudSiteHost]);

  const fetchAvailableProductIdsForCloudSite = useCallback(
    async (cloudSiteId: string) => {
      setFetchingProductIds(true);
      setErrorForFetchingCloudSiteProductsIds(null);
      try {
        const existingProductsIdResponse = await fetchAvailableProductIdsOnCloudSite({
          cloudSiteIds: [cloudSiteId],
          transactionAccountId: txaId,
          isJiraOnTransferOperation: isJiraOnTransferOperationForCloudSite,
          fetchSandboxParentsData: isSandboxAppCreateInUOFEnabled && isSandboxCloudsite,
        });
        setExistingCloudSiteProductsIds(existingProductsIdResponse?.existingProductIds?.[cloudSiteId]);
        const sandboxParentProductIdsList = existingProductsIdResponse?.sandboxParentData?.[cloudSiteId]
          ? Object.keys(existingProductsIdResponse?.sandboxParentData?.[cloudSiteId])
          : [];
        setExistingSandboxCloudSiteProductIds(sandboxParentProductIdsList);
        if (existingProductsIdResponse?.sandboxParentData) {
          actions.updateSandboxParentData(existingProductsIdResponse?.sandboxParentData);
        }
        setFetchingProductIds(false);
        setErrorForFetchingCloudSiteProductsIds(null);
      } catch (e) {
        const errorMessage = `Fetching of available products on site failed`;
        setErrorForFetchingCloudSiteProductsIds(errorMessage);
      } finally {
        // for success or failure stop the loading
        setFetchingProductIds(false);
      }
    },
    [txaId, isJiraOnTransferOperationForCloudSite]
  );

  const fetchAvailableProductIdsForCloudSiteUrl = useCallback(
    async (cloudSiteUrl: string) => {
      setFetchingProductIds(true);
      try {
        const {
          cloudSiteId,
          existingProductIds,
          applicableInvoiceGroupId: invoiceGroupId,
          existingFamilyContainerId,
          sourceSystem,
          sandboxParentData,
        } = await fetchAvailableProductIdsByCloudSiteUrl({
          cloudSiteUrl,
          transactionAccountId: txaId,
          isJiraOnTransferOperation: true,
          fetchSandboxParentsData: isSandboxAppCreateInUOFEnabled,
        });
        if (cloudSiteId && sourceSystem === SourceSystem.CCP) {
          const { data: entitlementsResponse } = await fetchEntitlementsForCloudSite({
            variables: {
              id: txaId,
              filterOptions: [
                {
                  key: 'cloudSiteId',
                  value: cloudSiteId,
                },
                ...(commonEntitlementQueryFilters(true) || []),
              ],
              searchOptions: {
                first: maxThresholdForResults, // Disclaimer: Max threshold for the query
                offset: 0,
              },
              isRestricted: false,
              fetchECCFromIdentity: false,
            },
          });
          const productSubscriptions = entitlementsResponse?.transactionAccount?.productSubscriptions;
          const productsAndAppData = getDataForStore({
            entitlementSubscriptionsWrapper: productSubscriptions,
            offeringsAndpricingPlans: offeringsAndPricingPlans,
            operationType: OperationType.OWN,
            isAddOnsPhase2Enabled,
          });
          const externalCloudSiteProductsAndApps = convertToExternalCloudSites(productsAndAppData);

          if (productsAndAppData) {
            actions.addProductsAndApps(externalCloudSiteProductsAndApps);
          }
          if (invoiceGroupId) {
            actions.setInvoiceGroup(invoiceGroupId);
            actions.setManualInvoiceGroupId(invoiceGroupId);
          }
        }
        setCloudSiteIdOfTheUserSelectedCloudSite(cloudSiteId);
        setExistingCloudSiteProductsIds(existingProductIds);
        const sandboxParentProductIdsList = sandboxParentData?.[cloudSiteId]
          ? Object.keys(sandboxParentData?.[cloudSiteId])
          : [];
        setExistingSandboxCloudSiteProductIds(sandboxParentProductIdsList);
        if (sandboxParentData) {
          actions.updateSandboxParentData(sandboxParentData);
        }
        setExistingFamilyContainerId(existingFamilyContainerId);
      } finally {
        // for success or failure stop the loading
        setFetchingProductIds(false);
      }
    },
    [txaId]
  );

  const fetchUserSelectedCloudSiteProductIds = async (cloudSiteHostName: string | undefined) => {
    // in case the new cloud-site is selected we don't send any hostname string
    if (!cloudSiteHostName) {
      // check for any product / app already been added to the new cloud site than we need to restrict duplicate
      const existingProductIdsForNewSite =
        Object.values(products)
          .filter((product) => product.operationType === OperationType.NEW && isEmpty(product.cloudSite))
          .map(({ productId }) => productId) ?? [];
      setExistingCloudSiteProductsIds(existingProductIdsForNewSite);
      // open the drawer in that case
      openCatalogDrawer();
      return;
    }
    setUserSelectedCloudSiteHostname(cloudSiteHostName);

    // checking if already the cloud-site is present in the billing profile
    await fetchAvailableProductIdsForCloudSiteUrl(cloudSiteHostName);
    openCatalogDrawer();
  };

  const existingProductsForCloudSite = useProductsForCloudSiteStore({
    cloudSiteHost: cloudSiteHost || userSelectedCloudSiteHostname,
  });

  // if cloudsite id is not available then we need to get the cloudsite id from the existing products
  const selectedCloudSiteId = cloudSiteId
    ? cloudSiteId
    : existingProductsForCloudSite?.find((product) => product?.cloudSiteId)?.cloudSiteId;
  React.useEffect(() => {
    (async () => {
      // this is valid when cloud-site is available on mount of the Add New Button
      if (!selectedCloudSiteId) {
        return;
      }
      await fetchAvailableProductIdsForCloudSite(selectedCloudSiteId);
    })();
  }, [selectedCloudSiteId, fetchAvailableProductIdsForCloudSite]);

  const openCatalogDrawer = useCallback(() => setIsCatalogDrawerOpen(true), []);
  const closeCatalogDrawer = useCallback(() => setIsCatalogDrawerOpen(false), []);

  const openCloudSiteSelectorModal = useCallback(() => setIsCloudSiteSelectorOpen(true), []);
  const closeCloudsiteSelectorModal = useCallback(() => setIsCloudSiteSelectorOpen(false), []);

  const currentOperationTypes = useCurrentOperationTypesStore();
  const isTransferOrder = currentOperationTypes?.includes(OperationType.TRANSFER);

  const availableCloudsiteHostnames = useMemo(() => {
    const productsGroupedByCloudsite = groupBy(products, 'cloudSite');
    const allCloudsites = Object.keys(productsGroupedByCloudsite);

    return allCloudsites;
  }, [products]);

  const { addNewProductToCart } = useAddNewProductStore();

  // Finding jira products under same the cloud site to get family container Id
  const containerIdFromExistingJiraProducts = useMemo(
    () =>
      getContainerIdFromExistingJiraProducts({
        existingProductsForCloudSite,
        existingFamilyContainerId,
        isJPDAsJiraFamilyEnabled,
      }),
    [existingProductsForCloudSite, existingFamilyContainerId, isJPDAsJiraFamilyEnabled]
  );

  const existingJiraProduct = useMemo(
    () => getExistingJiraProduct({ existingProductsForCloudSite, isJPDAsJiraFamilyEnabled }),
    [existingProductsForCloudSite, isJPDAsJiraFamilyEnabled]
  );

  // 'isSandbox' field is true from backend for Rovo too, need a generic name for it.
  // FIXME :: https://hello.jira.atlassian.cloud/browse/ITFO-22564
  const isSandboxCloudsite = existingProductsForCloudSite.some(
    (product) => product?.isSandbox && product.productId !== ROVO_PRODUCT_ID
  );

  const isAddNewDisabledForSandboxCloudsite = isSandboxAppCreateInUOFEnabled ? false : isSandboxCloudsite;

  const isAddNewProductButtonDisabled = useMemo(() => {
    return (
      isLoading ||
      isDisabled ||
      isFetchingProductIds ||
      !!errorForFetchingCloudSiteProductsIds ||
      isAddNewDisabledForSandboxCloudsite
    );
  }, [
    isLoading,
    isDisabled,
    isTransferOrder,
    isFetchingProductIds,
    errorForFetchingCloudSiteProductsIds,
    isAddNewDisabledForSandboxCloudsite,
  ]);

  const entitlementNumbersUnderCloudSite = keyBy(existingProductsForCloudSite, 'entitlementNumber');

  const productIdsUnderCloudSite = keyBy(existingProductsForCloudSite, 'productId');

  /*
   * entitlementDetailsMap is used to show entitlement details in the app cart item of the catalogue page
   * jiraContainerProductIdOrProductId is different than jira familyContainerId
   * JIRA_CONTAINER_PRODUCT_ID is consider as common jira product id where all jira offerings are linked to this product id.
   * We cannot identify the jira product from the offerings. So we need to assign the family container relationship id.
   * Here is the complete documentation for more details https://hello.atlassian.net/wiki/spaces/COMITFO/pages/3322005219/UFO+auto+populate+apps+parent+product+in+catalog+page
   */
  const entitlementDetailsMap: Record<string, ParentEntitlementDetails> = useMemo(() => {
    const entitlementDetailsMap: Record<string, ParentEntitlementDetails> = {};

    // entitlements is used to get the entitlement details from the entitlementIdUnderCloudSite
    const entitlements = Object.values(productIdsUnderCloudSite);
    const maxJiraUserEntitlement = entitlements?.reduce((currentMax: number, entitlement) => {
      const recurringEstimatedUsers = estimatedUsersForRecurringBillEstimate?.[entitlement.id];
      const entitlementActiveUsers = recurringEstimatedUsers
        ? Number(recurringEstimatedUsers)
        : Number(entitlement.activeUsers);
      if (
        isProductBelongsToJiraFamily(entitlement.productId, { isJPDAsJiraFamilyEnabled }) &&
        currentMax < entitlementActiveUsers
      ) {
        currentMax = Number(entitlementActiveUsers);
      }
      return currentMax;
    }, -1);
    entitlements.forEach((entitlement) => {
      /*
       * JIRA_CONTAINER_PRODUCT_ID is consider as common jira product id where all jira offerings are linked to this product id.
       * if the product is jira then we need to assign the family container relationship id.
       * if the product is not jira product then we need to get the product id.
       */
      const jiraContainerProductIdOrProductId = (() => {
        const isJiraProduct = isProductBelongsToJiraFamily(entitlement.productId, { isJPDAsJiraFamilyEnabled });
        if (isJiraProduct && !isExistingStandaloneJPDProduct(entitlement.offeringId)) {
          return JIRA_CONTAINER_PRODUCT_ID;
        }
        return entitlement.productId;
      })();

      /*
       * offeringName is used to get the edition name from the offeringsAndPricingPlans
       */
      const offeringName = offeringsAndPricingPlans?.[entitlement.productId]?.find(
        (offering) => offering?.id === entitlement.offeringId
      );

      const maxUnitCount = (() => {
        if (isProductBelongsToJiraFamily(entitlement.productId, { isJPDAsJiraFamilyEnabled })) {
          return maxJiraUserEntitlement;
        }
        if (estimatedUsersForRecurringBillEstimate?.[entitlement.id]) {
          return Number(estimatedUsersForRecurringBillEstimate[entitlement.id]);
        }
        return entitlement.activeUsers;
      })();

      entitlementDetailsMap[jiraContainerProductIdOrProductId] = {
        entitlementNumber: entitlement.entitlementNumber,
        id: entitlement.id,
        edition: offeringName?.offeringName,
        productId: entitlement.productId,
        logoId: entitlement.logoId,
        unitCount: entitlement.activeUsers,
        latestPeriodEndDate: entitlement.nextBillDate,
        productName: entitlement.name,
        familyContainerId: entitlement?.containerId ?? '',
        maxUnitCount,
      } as ParentEntitlementDetails;
    });
    return entitlementDetailsMap;
  }, [productIdsUnderCloudSite, offeringsAndPricingPlans, estimatedUsersForRecurringBillEstimate]);

  /*
   * OrderItems consists of lineItems, productTrialMap and appsPromotionsIdsMap
   * These data coming from catalogue context provider
   * onAddToCart will transform catalogue line items to state store items
   */
  const onAddToCart = (OrderItems: CatalogueContextItems) => {
    const { lineItems, productTrialMap, appsPromotionsIdsDetailsMap } = OrderItems;

    // Getting jira family container id from existing products. If not present then generating new uuid
    const jiraContainerId = containerIdFromExistingJiraProducts ?? generateUuid();

    const newApps: CartLinkedEntitlementsSchema[] = [];
    const newProducts: CartProductSchema[] = [];
    const cloudSiteId =
      cloudSiteIdOfTheUserSelectedCloudSite ?? defaultTo(get(existingProductsForCloudSite, '[0].cloudSiteId'), '');
    lineItems.forEach((lineItem: CartCatalogueLineItem) => {
      const isJiraProduct = lineItem?.isItAJiraProduct;
      const containerId = isJiraProduct ? jiraContainerId : undefined;
      const containerOrProductId = containerId ?? lineItem.lineItemId;
      if (lineItem.isApp) {
        const parentEntitlementId = lineItem.parentEntitlementId;
        const trialLengthInDays = defaultTo(productTrialMap?.get(lineItem?.productKey), '');
        const promoCodeDetail = defaultTo(appsPromotionsIdsDetailsMap?.get(lineItem?.productKey), {});
        const promotions = !isEmpty(promoCodeDetail) ? [promoCodeDetail] : [];
        const isParentNew = isNewEntitlement({ operationType: products[parentEntitlementId].operationType });
        lineItem.nextBillingTimestamp = lineItem.nextBillingTimestamp
          ? lineItem.nextBillingTimestamp
          : getTimestampFromLinkedProductsOrApps({ parentEntitlementId, products, linkedEntitlements });
        if (isParentNew) {
          /* here we are assigning empty string to identify the app is belongs to new parent product
           * this is required to assign the check in getAppsPayload for the condition of relates from Entitlement id
           */
          lineItem.parentEntitlementId = '';
        }

        const newApp = transformLineItemsToCartApp(lineItem, trialLengthInDays, promotions);
        newApps.push({
          ...newApp,
          containerOrProductId: isJiraProduct ? jiraContainerId : parentEntitlementId,
        });
        return;
      }

      const trialLengthInDays = defaultTo(productTrialMap?.get(lineItem?.productKey), '');

      const appItems = (() => {
        return lineItem?.appLineItems?.map((appLineItem) => {
          // getting trial length from the productTrialMap. Which is block scope
          const trialLengthInDays = defaultTo(productTrialMap?.get(appLineItem?.productKey), '');

          // getting apps promocode from the appsPromotionsIdsMap
          const promocodeDetail = defaultTo(appsPromotionsIdsDetailsMap?.get(appLineItem?.productKey), {});
          const promotions = !isEmpty(promocodeDetail) ? [promocodeDetail] : [];
          // adding this check same as we have if parent line item is directly added as app
          // this check is when app is added as child with any new product from catalog directly.
          appLineItem.nextBillingTimestamp = appLineItem.nextBillingTimestamp
            ? appLineItem.nextBillingTimestamp
            : isJiraProduct && existingJiraProduct
            ? getTimestampFromLinkedProductsOrApps({
                parentEntitlementId: existingJiraProduct.id,
                products,
                linkedEntitlements,
              })
            : '';
          const newApp = transformLineItemsToCartApp(appLineItem, trialLengthInDays, promotions);

          return {
            ...newApp,
            containerOrProductId,
          };
        });
      })();

      newApps.push(...appItems);

      newProducts.push(
        transformLineItemsToCartProduct({
          lineItem,
          containerId,
          trialLengthInDays,
          cloudSite: cloudSiteHost || userSelectedCloudSiteHostname,
          cloudSiteId,
          isExternalProduct: !!existingFamilyContainerId,
        })
      );
    });

    addNewProductToCart(newProducts, newApps);
    closeCatalogDrawer();
  };

  const handleAddNew = () => {
    // If `cloudSiteHost` is available, user has clicked on the `Add` btn on cloud site level. Here, we should open the default catalog drawer.
    if (cloudSiteHost || isNetNewSite) {
      if (handleAddNewAppsClick) {
        handleAddNewAppsClick({
          product,
          cloudSiteId: cloudSiteId ?? '',
          cloudSiteUrl: cloudSiteHost,
          onAddToCart: onAddToCart,
          sandboxCloudSiteParentProductIds: existingSandboxCloudSiteProductIds,
        });
        return;
      }
      openCatalogDrawer();

      return;
    }

    // If `cloudSiteHost` is undefined / falsy, user has clicked on `Add` btn without selecting a cloudsite. Here, we will allow the user to decide if it is a net new or if they want to add to an existing cloudsite

    openCloudSiteSelectorModal();
  };

  if (!nativeProduct?.data) return null;

  const productIdsForCloudSiteInLocalState = existingProductsForCloudSite.map(({ productId }) => productId);

  const containerOrProductIds = existingProductsForCloudSite?.map((product) => product?.containerId || product?.id);
  const appsIdsForCloudSiteInLocalState = containerOrProductIds?.flatMap((id) =>
    linkedEntitlements[id]?.map((app) => app?.productId)
  );

  /**
   * Merging with `existingCloudSiteProductsIds` as it can also contain previously added + saved product Ids
   * in this cloudsite belonging to the current and any other Txas.
   *
   * `productIdsForCloudSiteInLocalState` will contain any new product additions not saved yet,
   * including the ones in current Txa.
   *
   * `appsIdsForCloudSiteInLocalState` will contain any new apps additions not saved yet,
   * including the ones in current Txa.
   *
   * As there can be an overlap of productIds between `existingCloudSiteProductsIds`, `productIdsForCloudSiteInLocalState` and `appsIdsForCloudSiteInLocalState`,
   * we are generating a unique list using `uniq()` utility function.
   */
  const completeListOfExistingProductIds = uniq(
    existingCloudSiteProductsIds?.concat(
      productIdsForCloudSiteInLocalState,
      appsIdsForCloudSiteInLocalState,
      newSiteProductIds
    )
  );

  if (showCatalogueInModal) {
    return isFetchingProductIds ? (
      <div>
        {Array.from({ length: 8 }).map((_, index) => (
          <SkeletonItem key={index} isShimmering hasIcon />
        ))}
      </div>
    ) : (
      <CataloguePage
        nativeProduct={nativeProduct}
        onAddToCart={onAddToCart}
        existingCloudSiteProductIds={completeListOfExistingProductIds}
        allowedAppsParentEntitlements={Object.keys(entitlementNumbersUnderCloudSite)}
        cloudSitesEntitlements={entitlementDetailsMap}
        isSandboxCloudsite={isSandboxCloudsite}
        existingSandboxCloudSiteProductIds={existingSandboxCloudSiteProductIds}
        showProductsInModal={true}
        checkedProductsList={checkedProductsList}
        setCheckedProductsList={setCheckedProductsList}
      />
    );
  }

  const iconBeforeAddNewButton = (() => {
    if (errorForFetchingCloudSiteProductsIds) {
      return { iconBefore: <WarningIcon label="" primaryColor={Y300} size="medium" /> };
    }
    if (!hideIconBefore) {
      return { iconBefore: <AddIcon label="add-product" size="small" /> };
    }
    return {};
  })();

  return (
    <>
      <Tooltip content={tooltipContent ?? ''} position="bottom">
        <LoadingButton
          className="add-new-product-button"
          testId="add-new-product-button"
          {...iconBeforeAddNewButton}
          onClick={handleAddNew}
          isDisabled={isAddNewProductButtonDisabled}
          appearance={addNewProductBtnAppearance}
          title={errorForFetchingCloudSiteProductsIds ? errorForFetchingCloudSiteProductsIds : ''}
          isLoading={showLoadingIndicator && (isFetchingProductIds || isLoading)}
        >
          {!hideAddNewButtonLabel && addNewProductLabel}
        </LoadingButton>
      </Tooltip>
      <CloudSiteSelector
        availableCloudsiteHostnames={availableCloudsiteHostnames}
        isOpen={isCloudsiteSelectorOpen}
        isLoading={isFetchingProductIds}
        closeModal={closeCloudsiteSelectorModal}
        onChange={fetchUserSelectedCloudSiteProductIds}
      />
      <Drawer
        testId="catalogue-page-drawer"
        isOpen={isCatalogDrawerOpen}
        onClose={closeCatalogDrawer}
        width="full"
        isFocusLockEnabled={false}
      >
        <CataloguePage
          nativeProduct={nativeProduct}
          onAddToCart={onAddToCart}
          existingCloudSiteProductIds={completeListOfExistingProductIds}
          allowedAppsParentEntitlements={Object.keys(entitlementNumbersUnderCloudSite)}
          cloudSitesEntitlements={entitlementDetailsMap}
          isSandboxCloudsite={isSandboxCloudsite}
          existingSandboxCloudSiteProductIds={existingSandboxCloudSiteProductIds}
        />
      </Drawer>
    </>
  );
};
